/**
 * Lightweight client-side tracker that posts heartbeats to /api/reservation-progress
 * so the admin can see abandoned reservation funnels.
 *
 * Usage:
 *   import { trackProgress, markCompleted } from '../lib/reservationTracker';
 *   trackProgress({ current_step: 2, pack_id: '...', customer: {...} });
 *   markCompleted();
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'phocearent_funnel_session';

const getOrCreateSessionId = () => {
  let sid = null;
  try { sid = localStorage.getItem(SESSION_KEY); } catch (e) { /* private mode */ }
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem(SESSION_KEY, sid); } catch (e) { /* ignore */ }
  }
  return sid;
};

let lastPayload = null; // debounce identical writes
let debounceTimer = null;

const sendNow = async (payload) => {
  try {
    await axios.post(`${API}/reservation-progress`, payload);
  } catch (e) {
    // Silent — tracker must never break the booking flow.
  }
};

export const trackProgress = (data = {}) => {
  const payload = { session_id: getOrCreateSessionId(), ...data };
  const key = JSON.stringify(payload);
  if (key === lastPayload) return; // no-op if nothing changed
  lastPayload = key;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => sendNow(payload), 600);
};

export const markCompleted = () => {
  const sid = getOrCreateSessionId();
  // Fire-and-forget; use keepalive so it still goes through on navigation.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob(
        [JSON.stringify({ session_id: sid, current_step: 5, is_completed: true })],
        { type: 'application/json' },
      );
      navigator.sendBeacon(`${API}/reservation-progress`, blob);
    } else {
      sendNow({ session_id: sid, current_step: 5, is_completed: true });
    }
  } catch (e) { /* ignore */ }
  // Reset the local session so a new attempt starts fresh
  try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
};
